/* eslint-disable indent */
'use client';

import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import JsonLdScript from './json-ld-script';

export default function DynamicJsonLd({
  type = 'all',
  pathname: propPathname,
  data,
}) {
  const pathname = usePathname();
  const path = propPathname || pathname;
  const baseUrl = 'https://www.x-space.ae/';
  const fullUrl = `${baseUrl}${path}`;

  // Move all hooks to the top before any conditional return
  const websiteSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'X-SPACE Real Estate Dubai',
      url: baseUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${baseUrl}/properties/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    }),
    [baseUrl]
  );

  const organizationSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'X-SPACE Real Estate',
      url: baseUrl,
      logo: 'https://images.x-space.ae/assets/images/Logo-B.png',
      sameAs: [
        'https://www.facebook.com/xspacerealestate',
        'https://www.instagram.com/xspace_realestate',
        'https://www.linkedin.com/feed/update/urn:li:activity:7176518870264938498',
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+971-4-3965199',
        contactType: 'customer service',
        areaServed: 'AE',
        availableLanguage: ['English', 'Arabic', 'Russian', 'Hindi'],
      },
    }),
    [baseUrl]
  );

  const localBusinessSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'RealEstateAgent',
      name: 'X-SPACE Real Estate',
      image: 'https://images.x-space.ae/assets/images/Aboutus pic/Company.webp',
      '@id': baseUrl,
      url: baseUrl,
      telephone: '+971-4-3965199',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Dubai-Showroom - 13 13 Ras Al Khor Rd - Nadd Al Hamar - Dubai',
        addressLocality: 'Dubai',
        postalCode: '00000',
        addressCountry: 'AE',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 25.185776930987412,
        longitude: 55.372077338460535,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: [
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
          ],
          opens: '09:30',
          closes: '18:30',
        },
      ],
      priceRange: '$$',
    }),
    [baseUrl]
  );

  const getBreadcrumbSchema = useMemo(() => {
    const pathSegments = path.split('/').filter(Boolean);
    const breadcrumbItems = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: baseUrl,
      },
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      if (!segment.includes('[') && !segment.includes(']')) {
        breadcrumbItems.push({
          '@type': 'ListItem',
          position: index + 2,
          name:
            segment.charAt(0).toUpperCase() +
            segment.slice(1).replace(/-/g, ' '),
          item: `${baseUrl}${currentPath}`,
        });
      }
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems,
    };
  }, [path, baseUrl]);

  // Only return after hooks are defined
  if (data) {
    return <JsonLdScript data={data} />;
  }

  switch (type) {
    case 'website':
      return <JsonLdScript data={websiteSchema} />;
    case 'organization':
      return <JsonLdScript data={organizationSchema} />;
    case 'localBusiness':
      return <JsonLdScript data={localBusinessSchema} />;
    case 'breadcrumb':
      return <JsonLdScript data={getBreadcrumbSchema} />;
    case 'all':
    default:
      return (
        <>
          <JsonLdScript data={websiteSchema} />
          <JsonLdScript data={organizationSchema} />
          <JsonLdScript data={localBusinessSchema} />
          <JsonLdScript data={getBreadcrumbSchema} />
        </>
      );
  }
}
